import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { toast } from "sonner";
import { getLocalStorage, removeItemFromLocalStorage, setLocalStorage } from "~/lib/helperFuncation/localStorageService";
import { removeSession } from "~/lib/helperFuncation/sessionStorageService";
import API_PATH from "~/utils/api-routes";
import { AUTH_COOKIE_TOKEN, RECAPTCHA_TOKEN, USER_IS_PASSWORD } from "~/utils/constant";
import { deleteCookie, getCookie } from "~/utils/cookies";
import { createSmkApi, setLatestOtpToken } from "~/utils/smkInterseptor";
interface InitialStateData {
  isDialogOpen: boolean;
  isGotoOtpVerification: boolean;
  loading: boolean;
  error: string | null;
  otpVerified: boolean;
  message: string;
  isPassword: boolean;
  setPassword: boolean;
  isUserLogin: boolean;
  isGotoForgotPasswordOtpVeification: boolean;
  isForgotPasswordOtpVerified: boolean;
  isPasswordReset: boolean;
  step: number;
  isShowCaptcha: boolean;
  otpToken: string | null;
  passwordToken: string | null;
  authToken: string | null;
  navigatTo: string | null; // This is not used in the current code, but can be added if needed
  isLogoutLoading?: boolean; // Optional loading state for logout
  // Firebase state
  useFirebaseAuth: boolean; // Toggle between Firebase and backend auth
  firebaseToken: string | null; // Firebase ID token
  firebaseUid: string | null; // Firebase user UID
  firebasePhoneNumber: string | null; // Store phone number for Firebase auth
  shouldCloseDialog?: boolean; // Optional flag to indicate if dialog should close after login
  isFocus?: boolean;
  isFirebaseLogin?: boolean; // Flag to indicate if the current login is via Firebase
  isDisabledNextButton?: boolean; // Flag to disable next button during Firebase login flow
}

const initialState: InitialStateData = {
  isDialogOpen: false,
  isGotoOtpVerification: false,
  loading: false,
  error: null,
  otpVerified: false,
  isPassword: getLocalStorage(USER_IS_PASSWORD) || false,
  setPassword: false,
  isUserLogin: false,
  message: "",
  isGotoForgotPasswordOtpVeification: false,
  isForgotPasswordOtpVerified: false,
  isPasswordReset: false,
  step: 1,
  isShowCaptcha: false,
  otpToken: null,
  passwordToken: null,
  authToken: null,
  navigatTo: null, // This is not used in the current code, but can be added if needed
  isLogoutLoading: false,
  // Firebase state
  useFirebaseAuth: false, // Default to backend auth, can be toggled
  firebaseToken: null,
  firebaseUid: null,
  firebasePhoneNumber: null, // Store phone number for message display
  shouldCloseDialog: true,
  isFocus: false,
  isFirebaseLogin: true,
  isDisabledNextButton: false,
};

const smkService = createSmkApi();

export const getOtpTokenThunk = createAsyncThunk(
  "login/getOtpToken",
  async (
    { mobile, recaptchaToken, country_code }: { mobile: string; recaptchaToken: string; country_code: number },
    { getState, dispatch, rejectWithValue },
  ) => {
    try {
      const response = await smkService.post(API_PATH.AUTH_LOGIN, { mobile, recaptchaToken, country_code: String(country_code) });
      const otpToken = response?.data?.data?.otp_token;
      if (otpToken) dispatch(setOtpToken(otpToken));
      return { data: response.data?.data, message: response?.data?.message };
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || "Unknown error";
      return rejectWithValue(message);
    }
  },
);

export const resendOtpThunk = createAsyncThunk(
  "login/resendOtp",
  async (payload: { mobile: string; country_code: string; recaptchaToken: string }, { getState, rejectWithValue }) => {
    try {
      const response = await smkService.post(API_PATH.RESEND_OTP, payload);
      return response.data;
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      const message = err.response?.data?.message || err.message || "Unknown error";
      return rejectWithValue(message);
    }
  },
);

export const verifyOtpThunk = createAsyncThunk(
  "login/verifyOtp",
  async (payload: { mobile: string; otp: string; country_code: string }, { getState, dispatch, rejectWithValue }) => {
    try {
      const response = await smkService.post(API_PATH.VERIFY_OTP, payload);
      const smkToken = response?.data?.data?.smkToken;
      if (smkToken) {
        dispatch(setAuthToken(smkToken));
      }
      return response.data.data;
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      const message = err.response?.data?.message || err.message || "Unknown error";
      return rejectWithValue(message);
    }
  },
);

export const setPasswordThunk = createAsyncThunk(
  "login/createPassword",
  async (payload: { password: string; confirmPassword: string }, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const token = getCookie(AUTH_COOKIE_TOKEN);
      setLatestOtpToken(token ?? "");
      const response = await smkService.post(API_PATH.SET_PASSWORD, payload);
      return response.data;
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      const message = err.response?.data?.message || err.message || "Unknown error";
      return rejectWithValue(message);
    }
  },
);

export const changePasswordThunk = createAsyncThunk(
  "login/changePassword",
  async (payload: { oldPassword: string; password: string; confirmPassword: string }, { getState, rejectWithValue }) => {
    try {
      const token = getCookie(AUTH_COOKIE_TOKEN);
      setLatestOtpToken(token ?? "");
      const response = await smkService.post(API_PATH.CHANGE_PASSWORD, payload);
      return response.data;
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      const message = err.response?.data?.message || err.message || "Unknown error";
      return rejectWithValue(message);
    }
  },
);

export const verifyPasswordThunk = createAsyncThunk(
  "login/verifyPassword",
  async (payload: { password: string }, { getState, dispatch, rejectWithValue }) => {
    try {
      const response = await smkService.post(API_PATH.VERIFY_PASSWORD, payload);
      const smkToken = response?.data?.data?.smkToken;
      if (smkToken) {
        dispatch(setAuthToken(smkToken));
      }
      return response.data.data;
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      const message = err.response?.data?.message || err.message || "Unknown error";
      return rejectWithValue(message);
    }
  },
);

export const logoutThunk = createAsyncThunk("logout", async (_, { getState, rejectWithValue }) => {
  try {
    const token = getCookie(AUTH_COOKIE_TOKEN);
    setLatestOtpToken(token ?? "");
    const response = await smkService.post(API_PATH.LOGOUT);
    return response.data;
  } catch (error) {
    const err = error as AxiosError<{ message: string }>;
    const message = err.response?.data?.message || err.message || "Unknown error";
    return rejectWithValue(message);
  }
});
//forgot password thunk
export const getforgotPasswordOtpTokenThunk = createAsyncThunk(
  "login/getforgotPasswordOtpToken",
  async (payload: { mobile: string; country_code: string; recaptchaToken: string }, { getState, dispatch, rejectWithValue }) => {
    try {
      const response = await smkService.post(API_PATH.FORGOT_PASSWORD, payload);
      const passwordToken = response?.data?.data?.passwordToken;
      if (passwordToken) {
        dispatch(setPasswordToken(passwordToken));
      }
      return { data: response?.data?.data, message: response.data?.message }; // send useful data to fulfilled case
    } catch (error: any) {
      const err = error as AxiosError<{ message: string }>;
      const message = err.response?.data?.message || err.message || "Unknown error";
      return rejectWithValue(message);
    }
  },
);

//verify forgot password otp thunk
export const verifyForgotPasswordOtpThunk = createAsyncThunk(
  "login/verifyForgotPasswordOtp",
  async (payload: { mobile: string; otp: string }, { getState, rejectWithValue }) => {
    try {
      const response = await smkService.post(`${API_PATH.VERIFY_FORGOT_OTP}`, payload);
      return response.data.data;
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      const status = err.response?.status;

      // 👉 Specific handling for 307
      if (status === 307) {
        return rejectWithValue("Request was redirected (307). Check API URL or CORS settings.");
      }

      const message = err.response?.data?.message || err.message || "Unknown error";
      return rejectWithValue(message);
    }
  },
);

//resend forgot otp thunk
export const resendForgotPasswordOtpThunk = createAsyncThunk(
  "login/resendForgotOtp",
  async (recaptchaToken: { mobile: string; country_code: string; recaptchaToken: string }, { getState, rejectWithValue }) => {
    try {
      const response = await smkService.post(API_PATH.RESEND_FORGOT_PASSWORD_OTP, recaptchaToken);
      return response.data;
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      const message = err.response?.data?.message || err.message || "Unknown error";
      return rejectWithValue(message);
    }
  },
);

//set password thunk
export const resetPasswordThunk = createAsyncThunk(
  "login/resetPassword",
  async (payload: { password: string; confirmPassword: string }, { getState, rejectWithValue }) => {
    try {
      const response = await smkService.post(API_PATH.RESET_PASSWORD, payload);
      return response.data;
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      const message = err.response?.data?.message || err.message || "Unknown error";
      return rejectWithValue(message);
    }
  },
);

type LoginResponse = {
  status: number;
  success: boolean;
  data: {
    contenttoken: string;
  };
  message: string;
};

export const loginWithFirebaseVerifyToken = createAsyncThunk<
  LoginResponse,
  { mobile: string; recaptchaToken: string; country_code: string; fbToken: string }
>("login/loginWithFirebaseVerifyToken", async (payload) => {
  const { fbToken, ...rest } = payload;
  setLatestOtpToken(fbToken ?? "");
  const response = await smkService.post(API_PATH.FIREBASE_VERIFY_TOKEN, rest);
  return response.data;
});

const loginSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    setIsDialogOpen: (state) => {
      state.isDialogOpen = true;
    },

    setIsDialogClose: (state) => {
      state.isDialogOpen = false;
    },
    setIsGotoVerificationTrue: (state) => {
      state.isGotoOtpVerification = true;
    },
    setIsGotoVerificationFalse: (state) => {
      state.isGotoOtpVerification = false;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    setMessage: (state, action: PayloadAction<string>) => {
      state.message = action.payload;
    },
    setIsUserLoginTrue: (state) => {
      state.isUserLogin = true;
    },
    setIsUserLoginFalse: (state) => {
      state.isUserLogin = false;
    },
    setIsSetPasswordTrue: (state) => {
      state.setPassword = true;
      setLocalStorage(USER_IS_PASSWORD, "true");
    },
    setIsPasswordFalse: (state) => {
      state.isPassword = false;
      setLocalStorage(USER_IS_PASSWORD, "false");
    },
    setIsOtpVerifiedFalse: (state) => {
      state.otpVerified = false;
    },
    setErrorNull: (state) => {
      state.error = null;
    },
    setLoginStep: (state, action: PayloadAction<number>) => {
      state.step = action.payload;
    },
    setIsShowCaptchaTrue: (state) => {
      state.isShowCaptcha = true;
    },
    setOtpToken: (state, action: PayloadAction<string>) => {
      state.otpToken = action.payload;
    },
    setPasswordToken: (state, action: PayloadAction<string>) => {
      state.passwordToken = action.payload;
    },
    setAuthToken: (state, action: PayloadAction<string | null>) => {
      state.authToken = action.payload;
    },
    setNavigatTo: (state, action: PayloadAction<string | null>) => {
      state.navigatTo = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    // Firebase reducers
    setUseFirebaseAuth: (state, action: PayloadAction<boolean>) => {
      state.useFirebaseAuth = action.payload;
    },
    setFirebaseToken: (state, action: PayloadAction<string | null>) => {
      state.firebaseToken = action.payload;
    },
    setFirebaseUid: (state, action: PayloadAction<string | null>) => {
      state.firebaseUid = action.payload;
    },
    setFirebasePhoneNumber: (state, action: PayloadAction<string | null>) => {
      state.firebasePhoneNumber = action.payload;
    },
    setShouldCloseDialog: (state, action: PayloadAction<boolean>) => {
      state.shouldCloseDialog = action.payload;
    },
    setIsFocus: (state, action: PayloadAction<boolean>) => {
      state.isFocus = action.payload;
    },
    setIsFirebaseLogin: (state, action: PayloadAction<boolean>) => {
      state.isFirebaseLogin = action.payload;
    },
    setIsDisabledNextButton: (state, action: PayloadAction<boolean>) => {
      state.isDisabledNextButton = action.payload;
    },
  },
  extraReducers: (builder) => {
    //getOtpTokenThunk
    builder
      .addCase(getOtpTokenThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getOtpTokenThunk.fulfilled, (state, action: PayloadAction<any>) => {
        const _isPassword = action.payload.data.is_password ? true : false;
        state.loading = false;
        state.isPassword = action.payload.data.is_password ? true : false;
        state.message = action.payload.message;
        state.step = action.payload.data.is_password === false ? 2 : 1;
        setLocalStorage(USER_IS_PASSWORD, _isPassword.toString());
      })
      .addCase(getOtpTokenThunk.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
      });

    //verifyOtpThunk
    builder
      .addCase(verifyOtpThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOtpThunk.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        state.otpVerified = true;
        state.isDialogOpen = false;
        state.isUserLogin = true;
        state.step = 1;
      })
      .addCase(verifyOtpThunk.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload || "OTP verification failed";
      });

    //resendOtpThunk
    builder
      .addCase(resendOtpThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resendOtpThunk.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(resendOtpThunk.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload || "OTP verification failed";
      });

    //setPasswordThunk
    builder
      .addCase(setPasswordThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(setPasswordThunk.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        state.isPassword = true;
        setLocalStorage(USER_IS_PASSWORD, "true");

        // state.step = 4;
      })
      .addCase(setPasswordThunk.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload || "Password do not match";
      });

    //changePasswordThunk
    builder
      .addCase(changePasswordThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changePasswordThunk.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(changePasswordThunk.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload || "Failed to change password";
      });

    //verifyPasswordThunk
    builder
      .addCase(verifyPasswordThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyPasswordThunk.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        state.isDialogOpen = false;
        state.isUserLogin = true;
      })
      .addCase(verifyPasswordThunk.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload || "Password do not match";
        state.isPassword = state.isPassword ? true : false;
      });

    //getforgotPasswordOtpTokenThunk
    builder
      .addCase(getforgotPasswordOtpTokenThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getforgotPasswordOtpTokenThunk.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.step = 2;
        state.isGotoForgotPasswordOtpVeification = true;
        state.message = action.payload.message;
      })
      .addCase(getforgotPasswordOtpTokenThunk.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload || "Password do not match";
      });

    //verifyForgotPasswordOtpThunk
    builder
      .addCase(verifyForgotPasswordOtpThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyForgotPasswordOtpThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(verifyForgotPasswordOtpThunk.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        if (action.payload?.includes?.("307")) {
          state.step = 3;
          state.isForgotPasswordOtpVerified = true;
          state.error = null;
        } else {
          state.error = action.payload || "OTP verification failed";
        }
      });

    //resend forgot otp Thunk
    builder
      .addCase(resendForgotPasswordOtpThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resendForgotPasswordOtpThunk.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(resendForgotPasswordOtpThunk.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload || "OTP verification failed";
        state.otpVerified = false;
      });

    //resetPasswordThunk
    builder
      .addCase(resetPasswordThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPasswordThunk.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
        state.step = 1;
      })
      .addCase(resetPasswordThunk.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload || "Password do not match";
      });

    //logoutThunk
    builder
      .addCase(logoutThunk.pending, (state) => {
        state.isLogoutLoading = true;
        state.error = null;
      })
      .addCase(logoutThunk.fulfilled, (state) => {
        state.isLogoutLoading = false;
        state.isUserLogin = false;
        state.error = null;
        state.isDialogOpen = false;
        state.step = 1;
        state.isPassword = false;
        state.otpToken = null;
        state.passwordToken = null;
        state.authToken = null; // ✅ Clear token from Redux
        deleteCookie(AUTH_COOKIE_TOKEN);
        removeItemFromLocalStorage(USER_IS_PASSWORD);
        removeSession(RECAPTCHA_TOKEN);
        toast.success("Logged out successfully");
      })
      .addCase(logoutThunk.rejected, (state, action: PayloadAction<any>) => {
        state.isLogoutLoading = false;
        state.error = action.payload || "logout Failed Please Try again";
        state.setPassword = false;
      });
  },
});

export const {
  setIsDialogOpen,
  setIsDialogClose,
  setIsGotoVerificationTrue,
  setIsGotoVerificationFalse,
  setError,
  setMessage,
  setIsUserLoginTrue,
  setIsUserLoginFalse,
  setIsPasswordFalse,
  setIsOtpVerifiedFalse,
  setErrorNull,
  setIsSetPasswordTrue,
  setLoginStep,
  setIsShowCaptchaTrue,
  setOtpToken,
  setPasswordToken,
  setAuthToken,
  setNavigatTo,
  setLoading,
  // Firebase actions
  setUseFirebaseAuth,
  setFirebaseToken,
  setFirebaseUid,
  setFirebasePhoneNumber,
  setShouldCloseDialog,
  setIsFocus,
  setIsFirebaseLogin,
  setIsDisabledNextButton,
} = loginSlice.actions;

export default loginSlice.reducer;
